import { Dialog, Transition } from "@headlessui/react";
import { Fragment, useState, useEffect } from "react";
import { XMarkIcon, MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import { votersAPI } from "../../../services/api";
import { showError } from "../../../utils/notificationHelper";

function AssignVoterModal({ isOpen, onClose, precinct, onAssigned }) {
  const [fullName, setFullName] = useState("");
  const [dob, setDob] = useState("");
  const [address, setAddress] = useState("");
  const [isPWD, setIsPWD] = useState(false);
  const [isSenior, setIsSenior] = useState(false);
  const [isIlliterate, setIsIlliterate] = useState(false);
  const [precinctName, setPrecinctName] = useState(precinct?.name || "");
  const [selectedResident, setSelectedResident] = useState(null);

  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setPrecinctName(precinct?.name || "");
      setFullName("");
      setDob("");
      setAddress("");
      setIsPWD(false);
      setIsSenior(false);
      setIsIlliterate(false);
      setSearchResults([]);
      setSelectedResident(null);
    }
  }, [isOpen, precinct]);

  //  Search residents 
  const handleSearch = async (e) => {
    const value = e.target.value;
    setFullName(value);

    if (value.length < 1) {
      setSearchResults([]);
      return;
    }

    setIsSearching(true);
    try {
      const res = await votersAPI.searchResident(value);
      //console.log("API Response:", res.data);

      const residentData = res.data?.data?.searched_resident;

      if (Array.isArray(residentData)) {
        setSearchResults(residentData);
      } else if (residentData && typeof residentData === "object") {
        setSearchResults([residentData]);
      } else {
        setSearchResults([]);
      }
    } catch (err) {
      console.error("Error fetching resident:", err);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  // When a resident is selected, autofill
  const handleSelectVoter = (resident) => {
    let formattedDOB = "";
    if (resident.formatted_birthdate) {
      const [month, day, year] = resident.formatted_birthdate.split("-");
      formattedDOB = `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
    }

    setFullName(
      resident.full_name || `${resident.first_name} ${resident.last_name}`
    );
    setDob(formattedDOB || "");
    setAddress(resident.full_address || "");
    setIsPWD(!!resident.pwd_status);
    setIsSenior(!!resident.senior_citizen_status);
    setIsIlliterate(!!resident.illiterate_status);
    setSelectedResident(resident);
    setSearchResults([]);
  };

  const handleAssign = async () => {
    try {
      if (!selectedResident) {
        console.warn("No resident selected for assignment.");
        onClose();
        return;
      }

      const voterId = selectedResident.voter_id || selectedResident?.voter?.voter_id;
      const precinctId = precinct?.precinct_id;

      if (!voterId) {
        showError("The resident is not a registered voter");
        return;
      }

      if (!precinctId) {
        console.warn("No precinct_id provided. Skipping update.");
        onClose();
        return;
      }

      await votersAPI.updateVoter(voterId, {
        precinct_id: precinctId,
      });
      if (typeof onAssigned === "function") {
        await onAssigned();
      }
    } catch (err) {
      console.error("Error assigning voter to precinct:", err);
    } finally {
      onClose();
    }
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/30" />
        </Transition.Child>

        <div className="fixed inset-0 flex items-center justify-center p-6">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-200"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100"
            leave="ease-in duration-150"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
          >
            <Dialog.Panel className="w-full max-w-5xl transform overflow-hidden rounded-2xl bg-white p-6 text-left shadow-xl transition-all">
              {/* Top divider + header */}
              <div className="border-b border-gray-300 w-[calc(100%+48px)] -mx-6 pb-3 mb-4 flex items-center justify-between">
                <Dialog.Title
                  className="flex items-center text-sm"
                >
                  <span className="inline-block px-3 py-1 bg-green-100 text-green-600 rounded-md mr-2 ml-4">
                    {precinct?.name}
                  </span>
                  Assign Voter to Precinct
                </Dialog.Title>
                <button
                  onClick={onClose}
                  className="p-2 rounded-full hover:bg-gray-100 transition-transform transform hover:rotate-90 duration-200 mr-4"
                >
                  <XMarkIcon className="w-6 h-6 text-gray-500" />
                </button>
              </div>

              {/* Form */}
              <div className="grid grid-cols-2 gap-4">
                <div className="relative">
                  <label className="text-sm font-medium">
                    Full Name <span className="text-red-500">*</span>
                  </label>
                  <div className="relative mt-1">
                    <MagnifyingGlassIcon className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search for voter by name"
                      value={fullName}
                      onChange={handleSearch}
                      className="w-full border rounded-md pl-10 pr-3 py-2"
                      autoComplete="off"
                    />
                  </div>
                  {/* Dropdown */}
                  {!isSearching && searchResults.length > 0 && (
                    <ul className="absolute z-10 bg-white border rounded-md mt-1 w-full max-h-40 overflow-y-auto shadow">
                      {searchResults.map((resident, idx) => (
                        <li
                          key={idx}
                          onClick={() => handleSelectVoter(resident)}
                          className="px-3 py-2 hover:bg-green-100 cursor-pointer"
                        >
                          {resident.full_name ||
                            `${resident.first_name} ${resident.last_name}`}
                        </li>
                      ))}
                    </ul>
                  )}
                  {isSearching && (
                    <div className="absolute mt-1 text-gray-400 text-sm px-2">
                      Searching...
                    </div>
                  )}
                </div>

                <div>
                  <label className="text-sm font-medium">Date of Birth</label>
                  <input
                    type="date"
                    value={dob}
                    onChange={(e) => setDob(e.target.value)}
                    className="w-full border rounded-md px-3 py-2 mt-1"
                  />
                </div>

                <div>
                  <label className="text-sm font-medium">Address</label>
                  <input
                    type="text"
                    placeholder="House No., Street, Barangay, City, Province"
                    value={address}
                    onChange={(e) => setAddress(e.target.value)}
                    className="w-full border rounded-md px-3 py-2 mt-1"
                  />
                </div>

                <div>
                  <label className="text-sm font-medium">
                    Precinct Assignment
                  </label>
                  <input
                    type="text"
                    value={precinctName}
                    readOnly
                    className="w-full border rounded-md px-3 py-2 mt-1 bg-white text-gray-700 cursor-not-allowed"
                  />
                </div>
              </div>

              {/* Checkboxes */}
              <div className="flex justify-center gap-14 mt-6">
                <label className="flex items-center gap-2 border px-5 py-3 rounded-lg w-72">
                  <input
                    type="checkbox"
                    checked={isPWD}
                    onChange={() => setIsPWD(!isPWD)}
                    className="h-4 w-4"
                  />
                  <span className="text-sm font-medium">
                    Person with Disability (PWD)
                  </span>
                </label>

                <label className="flex items-center gap-2 border px-5 py-3 rounded-lg w-72">
                  <input
                    type="checkbox"
                    checked={isSenior}
                    onChange={() => setIsSenior(!isSenior)}
                    className="h-4 w-4"
                  />
                  <span className="text-sm font-medium">Senior Citizen</span>
                </label>

                <label className="flex items-center gap-2 border px-5 py-3 rounded-lg w-72">
                  <input
                    type="checkbox"
                    checked={isIlliterate}
                    onChange={() => setIsIlliterate(!isIlliterate)}
                    className="h-4 w-4"
                  />
                  <span className="text-sm font-medium">Illiterate</span>
                </label>
              </div>

              {/* Footer */}
              <div className="mt-6">
                <div className="border-t border-gray-300 w-[calc(100%+48px)] -mx-6"></div>
                <div className="flex justify-end gap-2 mt-4">
                  <button
                    onClick={onClose}
                    className="px-4 py-2 bg-gray-100 rounded-md text-gray-700"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleAssign}
                    className="px-4 py-2 bg-green-600 text-white rounded-md"
                  >
                    Assign
                  </button>
                </div>
              </div>
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>
  );
}

export default AssignVoterModal;
